const DB_NAME    = "arctree_achievements";
const DB_VERSION = 1;
const STORE_NAME = "unlocks";

/** Achievement persistence contract — IndexedDB locally (this file), a
 *  server-backed implementation remotely (ServerPlatform). Interpreter is
 *  injected one of these rather than hardcoding AchievementStore. */
export interface AchievementSink {
  loadUnlocked(gameId: string): Promise<Set<string>>;
  markUnlocked(gameId: string, achievementId: string): Promise<void>;
}

export class AchievementStore implements AchievementSink {
  private _db: IDBDatabase | null = null;

  private _open(): Promise<IDBDatabase> {
    if (this._db) return Promise.resolve(this._db);
    return new Promise((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: ["gameId", "achievementId"] });
        }
      };
      req.onsuccess = () => { this._db = req.result; resolve(req.result); };
      req.onerror   = () => reject(req.error);
    });
  }

  async loadUnlocked(gameId: string): Promise<Set<string>> {
    const db = await this._open();
    return new Promise((resolve, reject) => {
      const tx    = db.transaction(STORE_NAME, "readonly");
      const store = tx.objectStore(STORE_NAME);
      const range = IDBKeyRange.bound([gameId, ""], [gameId, "￿"]);
      const req   = store.getAll(range);
      req.onsuccess = () =>
        resolve(new Set((req.result as { achievementId: string }[]).map((r) => r.achievementId)));
      req.onerror = () => reject(req.error);
    });
  }

  async markUnlocked(gameId: string, achievementId: string): Promise<void> {
    const db = await this._open();
    return new Promise((resolve, reject) => {
      const tx    = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      const req   = store.put({ gameId, achievementId, unlockedAt: Date.now() });
      req.onsuccess = () => resolve();
      req.onerror   = () => reject(req.error);
    });
  }
}
